import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Mail, Trash2, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { memberApi } from "@/resources/member";
import { accountApi } from "@/resources/account";
import { handleApiError, formatRelativeTime } from "@/utils";
import { Member } from "@/types";
import { ACCOUNT_ROLE } from "@/constants";
import { roleConfig, getMemberDisplayName, getMemberInitials } from "./constants";
import { EditMemberDialog } from "./team-members/EditMemberDialog";

export function TeamMembersTab() {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState(ACCOUNT_ROLE.EDITOR);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [editRole, setEditRole] = useState<string>("");
  const [editFirstName, setEditFirstName] = useState<string>("");
  const [editLastName, setEditLastName] = useState<string>("");
  const [editEmail, setEditEmail] = useState<string>("");
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);

  const { data: membersData, isLoading: isMembersLoading } = memberApi.useGetMembers();
  const { data: account } = accountApi.useGetAccount();
  const { mutate: inviteMember, isPending: isInviting } = memberApi.useInviteMember();
  const { mutate: removeMember, isPending: isRemoving } = memberApi.useRemoveMember();
  const { mutate: cancelInvitation, isPending: isCancelling } = memberApi.useCancelInvitation();
  const { mutate: resendInvitation, isPending: isResending } = memberApi.useResendInvitation();
  const { mutate: updateMember, isPending: isUpdatingMember } = memberApi.useUpdateMember();

  const members = membersData?.members ?? [];

  const handleInvite = () => {
    if (!inviteEmail) return;
    inviteMember(
      { email: inviteEmail, role: inviteRole },
      {
        onSuccess: () => {
          toast.success("Invitation sent", { description: `Invited ${inviteEmail} as ${inviteRole}` });
          setInviteEmail("");
        },
        onError: (error) => handleApiError(error),
      }
    );
  };

  const handleRemove = (member: Member) => {
    removeMember(member.id, {
      onSuccess: () => {
        toast.success("Member removed", { description: `${getMemberDisplayName(member)} has been removed.` });
        setMemberToRemove(null);
      },
      onError: (error) => {
        handleApiError(error);
        setMemberToRemove(null);
      },
    });
  };

  const handleCancelInvitation = (member: Member) => {
    cancelInvitation(member.id, {
      onSuccess: () => {
        toast.success("Invitation cancelled", { description: `Invitation to ${member.email} has been cancelled.` });
      },
      onError: (error) => handleApiError(error),
    });
  };

  const handleResendInvitation = (member: Member) => {
    resendInvitation(member.id, {
      onSuccess: () => {
        toast.success("Invitation resent", { description: `Invitation resent to ${member.email}.` });
      },
      onError: (error) => handleApiError(error),
    });
  };

  const openEditMember = (member: Member) => {
    setEditingMember(member);
    setEditRole(member.role);
    setEditFirstName(member.first_name ?? "");
    setEditLastName(member.last_name ?? "");
    setEditEmail(member.email);
  };

  const handleSaveMember = () => {
    if (!editingMember) return;
    updateMember(
      { id: editingMember.id, data: { role: editRole, first_name: editFirstName, last_name: editLastName, email: editEmail } },
      {
        onSuccess: () => {
          toast.success("Member updated", { description: `${getMemberDisplayName(editingMember)} has been updated.` });
          setEditingMember(null);
        },
        onError: (error) => handleApiError(error),
      }
    );
  };

  const canEditOrRemove = (member: Member): boolean => {
    if (member.role === ACCOUNT_ROLE.OWNER) return false;
    if (account && member.type === "user" && member.id === account.id) return false;
    return true;
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Team Members</CardTitle>
                <CardDescription>{members.length} members</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {isMembersLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isMembersLoading && members.map((member) => {
              const rc = roleConfig[member.role] ?? roleConfig.viewer;
              const displayName = getMemberDisplayName(member);
              const initials = getMemberInitials(member);
              const editable = canEditOrRemove(member);
              const isPending = member.status === "pending";

              return (
                <div
                  key={member.id}
                  onClick={() => editable && !isPending && openEditMember(member)}
                  className={`flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/20 transition-colors ${editable && !isPending ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary-tint-soft flex items-center justify-center text-xs font-semibold text-primary">
                      {initials}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{displayName}</p>
                        {isPending && (
                          <Badge variant="secondary" className="text-xs border-0 bg-amber-500/10 text-amber-600">
                            Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {member.type === 'user' && (
                      <span className="text-xs text-muted-foreground/60">
                        Last active {formatRelativeTime(member.last_active_at)}
                      </span>
                    )}
                    <Badge variant="secondary" className={`border-0 text-xs capitalize ${rc.color}`}>
                      {rc.label}
                    </Badge>
                    {isPending && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                        disabled={isResending}
                        onClick={(e) => { e.stopPropagation(); handleResendInvitation(member); }}
                        title="Resend invitation"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {editable && isPending && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        disabled={isCancelling}
                        onClick={(e) => { e.stopPropagation(); handleCancelInvitation(member); }}
                        title="Cancel invitation"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {editable && !isPending && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        disabled={isRemoving}
                        onClick={(e) => { e.stopPropagation(); setMemberToRemove(member); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invite Team Member</CardTitle>
              <CardDescription>Add new members to your workspace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Email Address</Label>
                <ClearableInput
                  value={inviteEmail}
                  onChange={setInviteEmail}
                  onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                  placeholder="colleague@company.com"
                  className="h-9 rounded-lg bg-card"
                  icon={null}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Role</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as ACCOUNT_ROLE)}>
                  <SelectTrigger className="h-9 rounded-lg bg-card">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border border-border">
                    <SelectItem value={ACCOUNT_ROLE.ADMIN}>Admin</SelectItem>
                    <SelectItem value={ACCOUNT_ROLE.EDITOR}>Editor</SelectItem>
                    <SelectItem value={ACCOUNT_ROLE.VIEWER}>Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleInvite} size="sm" className="w-full h-9 gap-1.5" disabled={!inviteEmail || isInviting}>
                {isInviting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                Send Invite
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Role Permissions</CardTitle>
              <CardDescription>What each role can do</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(roleConfig).map(([key, rc]) => (
                <div key={key} className="flex items-start gap-2.5">
                  <Badge variant="secondary" className={`border-0 text-xs capitalize shrink-0 mt-0.5 ${rc.color}`}>
                    {rc.label}
                  </Badge>
                  <p className="text-xs text-muted-foreground">
                    {key === "owner" && "Full access. Cannot be removed."}
                    {key === "admin" && "Manage team, brands, and all settings."}
                    {key === "editor" && "Edit products, keywords, and run tests. No brand or workspace admin access."}
                    {key === "viewer" && "View-only access to all data."}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <EditMemberDialog
        editingMember={editingMember}
        onClose={() => setEditingMember(null)}
        editRole={editRole}
        onEditRoleChange={setEditRole}
        editFirstName={editFirstName}
        onEditFirstNameChange={setEditFirstName}
        editLastName={editLastName}
        onEditLastNameChange={setEditLastName}
        editEmail={editEmail}
        onEditEmailChange={setEditEmail}
        onSave={handleSaveMember}
        isUpdating={isUpdatingMember}
      />

      <ConfirmDeleteDialog
        open={!!memberToRemove}
        onOpenChange={(open) => { if (!open) setMemberToRemove(null); }}
        title={`Remove ${memberToRemove ? getMemberDisplayName(memberToRemove) : ""}?`}
        description="They will lose access to this workspace immediately. This action cannot be undone."
        confirmLabel="Remove"
        isPending={isRemoving}
        onConfirm={() => memberToRemove && handleRemove(memberToRemove)}
      />
    </>
  );
}
